import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import type { UploadableFile, UploadedFile } from '@core/file-upload';
import { isUploadedFile } from '@core/file-upload';
import { Entries } from '@innogy/utils-state';
import { Store } from '@ngrx/store';
import { TextField, JssModule } from '@sitecore-jss/sitecore-jss-angular';
import { FormArrayState, RemoveArrayControlAction } from 'ngrx-forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { CSCaptureDirective } from '@legacy/utils-contentsquare';
import { FileSizePipe } from '@innogy/utils-deprecated';

import { addFileAction } from './file-input.actions';
import { DragAndDropDirective } from '../drag-and-drop/drag-and-drop.directive';

@Component({
  selector:
    'wl-file-input[identifier][control][files][selectFileLabel][dragAndDropInstructionsLabel]',
  templateUrl: './file-input.component.html',
  styleUrls: [
    './file-input.component.ed.scss',
    './file-input.component.essent.scss',
  ],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    DragAndDropDirective,
    AutomationIdDirective,
    FaIconComponent,
    JssModule,
    CSCaptureDirective,
    AutomationIdDirective,
    FileSizePipe,
  ],
})
export class FileInputComponent {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() identifier!: string;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() control!: FormArrayState<string>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() files!: Entries<UploadableFile | UploadedFile>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public selectFileLabel!: TextField;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public dragAndDropInstructionsLabel!: TextField;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public fileListHeading?: TextField;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public allowedFileTypes?: string[];
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public allowMultiple = true;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public filePreviewMode: 'image' | 'list' = 'list';
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public changeFileLabel?: TextField;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() public dragAndDrop = true;

  @ViewChild('fileDropRef', { static: false })
  public fileDropElement!: ElementRef;

  @ViewChild('fileEditRef', { static: false })
  public fileDropEditElement!: ElementRef;

  constructor(
    private readonly store$: Store,
    private readonly domSanitizer: DomSanitizer
  ) {}

  public handleFileDragAndDrop(files: File[]) {
    this.handleAddFiles(this.filterDuplicateFiles(files));
  }

  public handleFileBrowser(event: Event) {
    const target = event.target as HTMLInputElement;

    if (target.files) {
      /**
       * Convert the FileList (which is returned from the file input element) to
       * a File array type so we can access its properties.
       */
      const files = Array.from(target.files);
      this.handleAddFiles(this.filterDuplicateFiles(files));
      if (this.fileDropElement?.nativeElement) {
        this.fileDropElement.nativeElement.value = '';
      }
      if (this.fileDropEditElement?.nativeElement) {
        this.fileDropEditElement.nativeElement.value = '';
      }
    }
  }

  public handleDeleteFile(index: number, fileName: string) {
    this.store$.dispatch(new RemoveArrayControlAction(this.control.id, index));
    Object.keys(this.files)
      .filter((key) => key.includes(fileName))
      .forEach((key) => {
        delete this.files[key];
      });
  }

  private filterDuplicateFiles(files: File[]) {
    const uniqueFiles = new Map<string, File>();

    files.forEach((file) => {
      if (
        !Object.values(this.files)
          .map((fileEntry) => fileEntry?.entry)
          .filter((fileEntry) => fileEntry !== undefined)
          .filter((fileEntry) => 'file' in fileEntry)
          .some((fileEntry) => fileEntry.file.name === file.name)
      ) {
        uniqueFiles.set(file.name, file);
      }
    });

    return Array.from(uniqueFiles.values());
  }

  private handleAddSingleFile(file: File) {
    const referenceId = `${this.control.id}-${file.name}`;
    if (file) {
      this.store$.dispatch(
        addFileAction({
          singleFile: true,
          controlId: this.control.id,
          file,
          referenceId,
        })
      );
    }
  }

  private handleAddMultipleFiles(files: File[]) {
    for (const file of files) {
      const referenceId = `${this.control.id}-${file.name}`;
      this.store$.dispatch(
        addFileAction({
          singleFile: false,
          controlId: this.control.id,
          file,
          referenceId,
        })
      );
    }
  }

  public handleAddFiles(files: File[]) {
    this.allowMultiple
      ? this.handleAddMultipleFiles(files)
      : this.handleAddSingleFile(files[0]);
  }

  public getFilePath(file: UploadableFile | UploadedFile) {
    return isUploadedFile(file)
      ? file.documentUrl
      : this.domSanitizer.bypassSecurityTrustResourceUrl(
          URL.createObjectURL(file.file)
        );
  }
}
